import {
  Component,
  OnDestroy,
  Input,
  forwardRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { debounceTime, finalize, takeUntil } from 'rxjs/operators';

import { IAppIntegrationLocation } from 'v2/modules/app-integration-account/intefaces/app-integration-locations.interface';

import { SnackBarConfig } from '../../configs/snackbar.config';
import { IPagination } from '../../interfaces/paginator.interface';
import { AppIntegrationAccountLocationApiService } from '../../services/api/app-integration/app-integration-account-location-api.service';
import { trackById } from '../../util/track-by.util';

@Component({
  selector: 'lum-external-warehouse-control',
  templateUrl: './external-warehouse-control.component.html',
  styleUrls: ['./external-warehouse-control.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ExternalWarehouseControlComponent),
      multi: true,
    },
  ],
})
export class ExternalWarehouseControlComponent
  implements OnDestroy, OnChanges, ControlValueAccessor
{
  @Input()
  disabled :boolean = false;

  @Input()
  control :FormControl;

  @Input()
  queryParams :{ [param :string] :string | string[] } = {};

  searchTerm :string = '';
  selectedItem :IAppIntegrationLocation | null = null;
  pagination :IPagination | null = null;
  records :IAppIntegrationLocation[] = [];
  previousSelectedItem :IAppIntegrationLocation | null = null;

  trackById = trackById;

  private readonly _loadingRecords = new BehaviorSubject<boolean>(<boolean>false);

  private onChange :any = () => {};
  private onTouched :any = () => {};

  private onDestroy$ = new Subject();

  constructor(
    private appIntegrationAccountLocationApiService :AppIntegrationAccountLocationApiService,
    private snackbar :MatSnackBar,
  ) {}

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  ngOnChanges(changes :SimpleChanges) :void {
    if (changes.queryParams && !changes.queryParams.firstChange) {
      this.pagination = null;
      this.records = [];
      this.resetValues();
    }
  }

  resetValues() {
    this.onChange(null);
  }

  writeValue(value :IAppIntegrationLocation | null) :void {
    this.selectedItem = value;
    this.onChange(value);
  }

  registerOnChange(fn :(value :IAppIntegrationLocation | null) => void) :void {
    this.onChange = fn;
  }

  registerOnTouched(fn :() => void) :void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled :boolean) :void {
    // You can implement this if you need to handle disabling the component
  }

  displaySelectedItem() :string {
    return this.selectedItem?.name || '';
  }

  onSelectItem(selectedItem :IAppIntegrationLocation) :void {
    const currentSelectedItem = selectedItem;
    if (currentSelectedItem.id === this.selectedItem?.id) {
      return;
    }

    this.onChange(currentSelectedItem);
  }

  onInputChange(keyword :string) :void {
    if (this.selectedItem) {
      if (this.previousSelectedItem?.name === keyword) {
        this.onChange(this.selectedItem);
      } else {
        this.resetValues();
      }
      this.previousSelectedItem = this.selectedItem;
    }

    this.pagination = null;
    this.records = [];
    this.searchTerm = keyword;
    this.getRecords();
  }

  getRecords() :void {
    if (
      this._loadingRecords.value ||
      (this.pagination &&
        this.pagination.current_page === this.pagination.current_page)
    ) {
      return;
    }

    this._loadingRecords.next(true);
    this.appIntegrationAccountLocationApiService
      .get({
        limit: '20',
        page: this.pagination ? (this.pagination.current_page + 1).toString() : '1',
        name: this.searchTerm,
        ...this.queryParams,
      })
      .pipe(
        debounceTime(1000),
        takeUntil(this.onDestroy$),
        finalize(() => {
          this._loadingRecords.next(false);
        }),
      )
      .subscribe({
        next: (response :any) => {
          this.pagination = response.meta;
          this.records = [...this.records, ...response.data];
        },
        error: (error :any) => {
          this.snackbar.open('Failed to load external warehouses', 'Close', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  get displayValue() :string {
    return this.selectedItem?.name || '';
  }

  get loadingRecords$() :Observable<any> {
    return this._loadingRecords.asObservable();
  }
}
